import "./text-message.css";
import { useState, useEffect, useRef } from "react";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { util } from "../helpers/common";
import { renderMarkdown } from "../helpers/markdownUtils";
import { useStrings } from '../i18n/useStrings';
import { sendReadAcknowledgement } from "../services/messagingService";
import { getConversationId } from "../services/dataProvider";

export default function TextMessage({conversationEntry}) {
    const strings = useStrings();
    const [acknowledgementTimestamp, setAcknowledgementTimestamp] = useState("");
    const [hasBeenRead, setHasBeenRead] = useState(false);
    const messageContentRef = useRef(null);
    const readTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const isIntersectingRef = useRef(false); // Track if message is currently visible in viewport
    const hasBeenReadRef = useRef(false); // Track read status for event handlers

    useEffect(() => {
        if (conversationEntry.isRead) {
            setAcknowledgementTimestamp(conversationEntry.readAcknowledgementTimestamp);
        } else if (conversationEntry.isDelivered) {
            setAcknowledgementTimestamp(conversationEntry.deliveryAcknowledgementTimestamp);
        } else if (conversationEntry.isSent) {
            setAcknowledgementTimestamp(conversationEntry.transcriptedTimestamp);
        }
    }, [conversationEntry]);

    // Send read acknowledgement when message becomes visible
    useEffect(() => {
        // Only send read acks for incoming messages (from agent/chatbot/system)
        if (conversationEntry.isEndUserMessage) {
            return;
        }

        // Don't send multiple read acks for the same message
        if (hasBeenRead) {
            hasBeenReadRef.current = true;
            return;
        }

        // Ensure message has a valid ID
        if (!conversationEntry.messageId) {
            return;
        }

        // Shared function to send read acknowledgement (reduces duplication)
        const sendReadAck = () => {
            if (hasBeenReadRef.current || !isMountedRef.current) {
                return;
            }
            
            const isWindowHidden = window.rdmWindowVisible === false;
            if (isWindowHidden) {
                return;
            }
            
            hasBeenReadRef.current = true;
            setHasBeenRead(true);
            const conversationId = getConversationId();
            sendReadAcknowledgement(conversationId, conversationEntry.messageId);
        };

        const handleVisibilityChange = () => {
            const isWindowHidden = window.rdmWindowVisible === false;
            
            if (isWindowHidden && readTimeoutRef.current) {
                clearTimeout(readTimeoutRef.current);
                readTimeoutRef.current = null;
            } else if (!isWindowHidden && !readTimeoutRef.current && isIntersectingRef.current && isMountedRef.current && !hasBeenReadRef.current) {
                readTimeoutRef.current = setTimeout(sendReadAck, 1000);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('rdmVisibilityChanged', handleVisibilityChange);

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    isIntersectingRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.5;
                    
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        if (hasBeenReadRef.current) {
                            return;
                        }
                        
                        const isWindowHidden = window.rdmWindowVisible === false;
                        
                        if (isWindowHidden) {
                            return;
                        }

                        readTimeoutRef.current = setTimeout(sendReadAck, 1000);
                    } else {
                        // If message disappears before 1 second, cancel
                        if (readTimeoutRef.current) {
                            clearTimeout(readTimeoutRef.current);
                            readTimeoutRef.current = null;
                        }
                    }
                });
            },
            {
                // Consider visible if 50% of message is visible
                threshold: 0.5,
                // Observe in main viewport
                root: null,
                // Start observing 50px before message becomes visible
                rootMargin: '50px'
            }
        );

        if (messageContentRef.current) {
            observer.observe(messageContentRef.current);
        }

        // Cleanup: stop observing when dependencies change or component unmounts
        return () => {
            isMountedRef.current = false;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('rdmVisibilityChanged', handleVisibilityChange);
            observer.disconnect();
            if (readTimeoutRef.current) {
                clearTimeout(readTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationEntry.messageId, conversationEntry.isEndUserMessage]);

    /**
     * Generates a classname for Text Message metadata such as sender text.
     * @returns {string}
     */
    function generateMessageSenderContentClassName() {
        const className = `textMessageSenderContent ${conversationEntry.isEndUserMessage ? `outgoing` : `incoming`}`;

        return className;
    }

    /**
     * Generates a classname for Text Message bubble container.
     * @returns {string}
     */
    function generateMessageBubbleContainerClassName() {
        const className = `textMessageBubbleContainer`;

        return className;
    }

    /**
     * Generates a classname for Text Message bubble ui.
     * @returns {string}
     */
    function generateMessageBubbleClassName() {
        const className = `textMessageBubble ${conversationEntry.isEndUserMessage ? `outgoing` : `incoming`}`;

        return className;
    }

    /**
     * Generates a classname for Text Message content (i.e. actual text).
     * @returns {string}
     */
    function generateMessageContentClassName() {
        const className = `textMessageContent ${conversationEntry.isEndUserMessage ? `outgoing` : `incoming`}`;

        return className;
    }

    /**
     * Generates a text with the message sender infomation.
     * @returns {string}
     */
    function generateMessageSenderContentText() {
        const formattedTime = util.getFormattedTime(conversationEntry.transcriptedTimestamp);

        return `${conversationEntry.isEndUserMessage ? strings.you : conversationEntry.actorName} ${strings.at} ${formattedTime}`;
    }

    /**
     * Generates text content with the message acknowledgement infomation.
     * @returns {string}
     */
    function generateMessageAcknowledgementContentText() {
        const formattedAcknowledgementTimestamp = util.getFormattedTime(acknowledgementTimestamp);

        if (conversationEntry.isEndUserMessage) {
            if (conversationEntry.isRead) {
                return `${strings.readAt} ${formattedAcknowledgementTimestamp} • `;
            } else if (conversationEntry.isDelivered) {
                return `${strings.deliveredAt} ${formattedAcknowledgementTimestamp} • `;
            } else if (conversationEntry.isSent) {
                return `${strings.sent} • `;
            }
        }
        return ``;
    }

    return (
        <>
            <p className={generateMessageSenderContentClassName()}>{generateMessageAcknowledgementContentText()}{generateMessageSenderContentText()}</p>
            <div className={generateMessageBubbleContainerClassName()}>
                <div className={generateMessageBubbleClassName()}>
                    <div
                        ref={messageContentRef}
                        className={generateMessageContentClassName()}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(ConversationEntryUtil.getTextMessageContent(conversationEntry)) }}
                    />
                </div>
            </div>
        </>
    );
}